// NOTE! For the first iteration, we are simply copying the implementation of Assignees
// It will soon be overhauled in Issue https://gitlab.com/gitlab-org/gitlab/-/issues/233736
import { defineAsyncComponent } from '../../../lib/utils/vue3compat/vue.js?vue3';
import { MountingPortal } from '../../../lib/utils/vue3compat/portal_vue_vue3.js?vue3';
import { GlButton } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { createAlert } from '../../../alert.js?vue3';
import { TYPE_ISSUE } from '~/issues/constants';
import { __ } from '~/locale';
import { InternalEvents } from '../../../tracking/index.js?vue3';
import { isGid, getIdFromGraphQLId } from '~/graphql_shared/utils';
import { fetchUserCounts } from '~/super_sidebar/user_counts_fetch';
import ReviewerDrawer from '../../../merge_requests/components/reviewers/reviewer_drawer.vue?vue3';
import { useBatchComments } from '../../../batch_comments/store/index.js?vue3';
import ReviewerTitle from '../../../../../../ee/app/assets/javascripts/sidebar/components/reviewers/reviewer_title.vue?vue3';
import Store from '~/sidebar/stores/sidebar_store';
import eventHub from '~/sidebar/event_hub';
import diffsEventHub from '~/diffs/event_hub';
import getMergeRequestReviewersQuery from '~/sidebar/queries/get_merge_request_reviewers.query.graphql';
import mergeRequestReviewersUpdatedSubscription from '~/sidebar/queries/merge_request_reviewers.subscription.graphql';
import { EVT_REVIEW_DRAWER_APPROVED, TRACKING_REVIEW_DRAWER_SUBMIT_APPROVED } from '~/diffs/constants';
import Reviewers from './reviewers.vue?vue3';
export default {
    name: 'SidebarReviewers',
    components: {
        MountingPortal,
        GlButton,
        ReviewerTitle,
        Reviewers,
        ReviewerDrawer,
        ApprovalSummary: defineAsyncComponent(()=>import('ee_component/merge_requests/components/reviewers/approval_summary.vue')),
        SuggestedReviewers: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/merge_requests/components/reviewers/suggested_reviewers.vue?vue3'))
    },
    mixins: [
        InternalEvents.mixin()
    ],
    inject: {
        aiSuggestedReviewersAvailable: {
            default: false
        }
    },
    props: {
        mediator: {
            type: Object,
            required: true
        },
        field: {
            type: String,
            required: true
        },
        issuableType: {
            type: String,
            required: false,
            default: TYPE_ISSUE
        },
        issuableIid: {
            type: String,
            required: true
        },
        projectPath: {
            type: String,
            required: true
        }
    },
    apollo: {
        issuable: {
            query: getMergeRequestReviewersQuery,
            variables () {
                return {
                    iid: this.issuableIid,
                    fullPath: this.projectPath
                };
            },
            update (data) {
                return data.namespace?.issuable;
            },
            result () {
                this.initialLoading = false;
            },
            error () {
                createAlert({
                    message: __('An error occurred while fetching reviewers.')
                });
            },
            subscribeToMore: {
                document () {
                    return mergeRequestReviewersUpdatedSubscription;
                },
                variables () {
                    return {
                        issuableId: this.issuable?.id
                    };
                },
                skip () {
                    return !this.issuable?.id;
                },
                updateQuery (_, { subscriptionData: { data: { mergeRequestReviewersUpdated } } }) {
                    if (mergeRequestReviewersUpdated) {
                        this.store.setReviewersFromRealtime(mergeRequestReviewersUpdated.reviewers.nodes.map((r)=>({
                                ...r,
                                id: getIdFromGraphQLId(r.id)
                            })));
                    }
                }
            }
        }
    },
    data () {
        return {
            issuable: undefined,
            loading: false,
            initialLoading: true,
            drawerOpen: false
        };
    },
    computed: {
        reviewers () {
            return this.issuable?.reviewers?.nodes || [];
        },
        isLoading () {
            return this.loading || this.$apollo.queries.issuable.loading;
        },
        canUpdate () {
            return this.issuable?.userPermissions?.adminMergeRequest || false;
        },
        canRerequest () {
            return this.issuable?.userPermissions?.updateMergeRequest || false;
        },
        isReviewer () {
            const { username } = this.store?.currentUser || {};
            return this.reviewers.some((reviewer)=>reviewer.username === username) || false;
        }
    },
    watch: {
        isReviewer: {
            handler (value) {
                useBatchComments().isReviewer = value;
            },
            immediate: true
        }
    },
    created () {
        this.store = new Store();
        this.removeReviewer = this.store.removeReviewer.bind(this.store);
        this.addReviewer = this.store.addReviewer.bind(this.store);
        this.removeAllReviewers = this.store.removeAllReviewers.bind(this.store);
        // Get events from deprecatedJQueryDropdown
        eventHub.$on('sidebar.removeReviewer', this.removeReviewer);
        eventHub.$on('sidebar.addReviewer', this.addReviewer);
        eventHub.$on('sidebar.removeAllReviewers', this.removeAllReviewers);
        eventHub.$on('sidebar.saveReviewers', this.saveReviewers);
        eventHub.$on('sidebar.toggleReviewerDrawer', this.toggleDrawerOpen);
        diffsEventHub.$on(EVT_REVIEW_DRAWER_APPROVED, this.reportReviewApprovedEvent);
    },
    beforeDestroy () {
        diffsEventHub.$off(EVT_REVIEW_DRAWER_APPROVED, this.reportReviewApprovedEvent);
        eventHub.$off('sidebar.removeReviewer', this.removeReviewer);
        eventHub.$off('sidebar.addReviewer', this.addReviewer);
        eventHub.$off('sidebar.removeAllReviewers', this.removeAllReviewers);
        eventHub.$off('sidebar.saveReviewers', this.saveReviewers);
        eventHub.$off('sidebar.toggleReviewerDrawer', this.toggleDrawerOpen);
    },
    methods: {
        reportReviewEvent (trigger, payload) {
            const { isReviewer } = this;
            const eventTriggers = {
                [EVT_REVIEW_DRAWER_APPROVED]: TRACKING_REVIEW_DRAWER_SUBMIT_APPROVED
            };
            if (eventTriggers[trigger]) this.trackEvent(eventTriggers[trigger], {
                reviewer: isReviewer ? 1 : 0,
                drafts: payload.comments,
                summary: payload.summary
            });
        },
        reportReviewApprovedEvent (payload) {
            this.reportReviewEvent(EVT_REVIEW_DRAWER_APPROVED, payload);
        },
        reviewBySelf () {
            // Notify gl dropdown that we are now assigning to current user
            this.$el.parentElement.dispatchEvent(new Event('assignYourself'));
            this.mediator.addSelfReview();
            this.trackEvent('assign_self_as_reviewer_in_mr');
            this.saveReviewers();
        },
        saveReviewers () {
            this.loading = true;
            this.mediator.saveReviewers(this.field).then(()=>{
                this.loading = false;
                fetchUserCounts();
                this.$apollo.queries.issuable.refetch();
            }).catch(()=>{
                this.loading = false;
                return createAlert({
                    message: __('Error occurred when saving reviewers')
                });
            });
        },
        requestReview (data) {
            this.mediator.requestReview(data);
        },
        async removeReviewerById (event) {
            const userId = isGid(event.userId) ? getIdFromGraphQLId(event.userId) : event.userId;
            this.store.reviewers = this.store.reviewers.filter((user)=>user.id !== userId);
            try {
                await this.saveReviewers();
            } catch (error) {
                createAlert(__('Unable to remove a reviewer at the moment, try again later'), {
                    error
                });
            } finally{
                event.done();
            }
        },
        toggleDrawerOpen (drawerOpen = !this.drawerOpen) {
            this.drawerOpen = drawerOpen;
            if (drawerOpen) {
                this.trackEvent('open_reviewer_sidebar_panel_in_mr');
            }
        }
    }
};
